import { ThemeEvents } from '@theme/events';
import { lockScroll, unlockScroll } from '@theme/utilities';

const CUSTOM_SIZE = 'Custom Size';
const EMPTY_LABEL = 'Select A Size';
const CHECK_ICON =
  '<span class="opt-state"><svg aria-hidden="true" viewBox="0 0 20 20" fill="none"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

class CsSizeDress extends HTMLElement {
  #controller = new AbortController();
  #formController = new AbortController();
  #outsideController;
  #data = [];
  #sizes = [];
  #selected = '';
  #unit = 'inch';
  #invalidMeasurementFocusScheduled = false;

  /** @type {HTMLElement | undefined} */
  #nativePropertiesContainer;

  connectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('invalid', this.#onInvalid, { capture: true, signal });
    this.addEventListener('close', this.#onDialogClose, { capture: true, signal });
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });

    this.#initialize();
    this.#bindForm();
  }

  #bindForm() {
    this.#formController.abort();
    this.#formController = new AbortController();
    this.#nativePropertiesContainer?.remove();
    this.#nativePropertiesContainer = undefined;

    const { signal } = this.#formController;
    const form = this.#getForm();
    if (!form) return;

    const propertiesContainer = document.createElement('span');
    propertiesContainer.hidden = true;
    propertiesContainer.dataset.csSizeNativeProperties = '';
    form.appendChild(propertiesContainer);
    this.#nativePropertiesContainer = propertiesContainer;
    this.#syncNativeProperties();

    form.addEventListener('formdata', this.#onFormData, { signal });
    form.addEventListener('click', this.#onPurchaseClick, {
      capture: true,
      signal,
    });
  }

  /** @returns {HTMLFormElement | null} */
  #getForm() {
    const quickAddContent = this.closest('#quick-add-modal-content');
    const quickAddForm = quickAddContent?.querySelector(
      'product-form-component form[data-type="add-to-cart-form"]'
    );
    if (quickAddForm instanceof HTMLFormElement) return quickAddForm;

    const control = this.querySelector('[data-cs-size-control]');
    return control instanceof HTMLSelectElement ? control.form : null;
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#formController.abort();
    this.#nativePropertiesContainer?.remove();
    this.#outsideController?.abort();
    const dialog = this.querySelector('[data-cs-dialog]');
    if (dialog) unlockScroll(dialog);
  }

  reset() {
    this.#initialize(true);
    this.#bindForm();
  }

  #initialize(reset = false) {
    const source = this.querySelector('[data-cs-size-data]')?.textContent;
    if (!source) return;

    try {
      this.#data = JSON.parse(source);
    } catch (error) {
      console.error('Unable to parse dress size data:', error);
      return;
    }

    this.#sizes = [CUSTOM_SIZE, ...this.#data.map(({ us }) => us)];
    if (reset || !this.#sizes.includes(this.#selected)) this.#selected = '';

    if (reset) {
      this.#unit = 'inch';
      this.#clearMeasurements();
      this.#clearValidation();
    }

    this.#renderOptions();
    this.#renderChart();
    this.#syncSize();
    this.#syncUnit();
  }

  #renderOptions() {
    for (const list of this.querySelectorAll('[data-cs-dropdown], [data-cs-dialog-list]')) {
      const fragment = document.createDocumentFragment();

      for (const size of this.#sizes) {
        const item = document.createElement('li');
        item.className = 'dropdown-item';
        item.dataset.csSizeOption = size;
        item.tabIndex = -1;
        item.setAttribute('role', 'option');
        item.innerHTML = `${CHECK_ICON}<span class="opt-label">${size}</span><span class="${
          size === CUSTOM_SIZE ? 'opt-badge free' : 'opt-badge'
        }">${this.#badge(size)}</span>`;
        fragment.appendChild(item);
      }

      list.replaceChildren(fragment);
    }

    const control = this.querySelector('[data-cs-size-control]');
    if (!(control instanceof HTMLSelectElement)) return;

    control.replaceChildren(
      new Option(EMPTY_LABEL, ''),
      ...this.#sizes.map((size) => new Option(size, size))
    );
  }

  #badge(size) {
    if (size === CUSTOM_SIZE) return 'Free';
    const row = this.#data.find(({ us }) => us === size);
    return row ? `${row.uk} / ${row.eu} / ${row.au}` : '';
  }

  #select(size) {
    if (!this.#sizes.includes(size)) return;

    const dialog = this.querySelector('[data-cs-dialog]');
    const mobileOpen = dialog instanceof HTMLDialogElement && dialog.open;

    if (size !== CUSTOM_SIZE) this.#clearMeasurements();
    this.#selected = size;
    if (size === CUSTOM_SIZE) {
      this.querySelector('[data-cs-custom-form] [data-cs-measure-box]')?.classList.add('hidden');
    }
    this.#syncSize();
    this.#clearValidation();
    this.#closeDesktop();
    this.#closeMobile(mobileOpen);
  }

  #syncSize() {
    const custom = this.#selected === CUSTOM_SIZE;
    const badge = this.#selected ? this.#badge(this.#selected) : '';

    this.querySelectorAll('[data-cs-size-text]').forEach((node) => {
      node.textContent = this.#selected || EMPTY_LABEL;
    });
    this.querySelectorAll('[data-cs-size-badge]').forEach((node) => {
      node.textContent = badge;
      node.classList.toggle('free', custom);
    });

    const control = this.querySelector('[data-cs-size-control]');
    if (control instanceof HTMLSelectElement) {
      control.value = this.#selected;
      control.removeAttribute('name');
    }

    this.querySelector('[data-cs-custom-form]')?.classList.toggle('hidden', !custom);
    this.querySelectorAll('[data-cs-custom]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const propertyName = input.dataset.csPropertyName || input.name;
      if (propertyName) input.dataset.csPropertyName = propertyName;

      input.disabled = !custom;
      input.removeAttribute('name');
      if (input.matches('[data-cs-measurement]')) input.required = custom;
    });

    for (const list of this.querySelectorAll('[data-cs-dropdown], [data-cs-dialog-list]')) {
      const options = [...list.querySelectorAll('[data-cs-size-option]')];
      const activeOption = options.find((option) => option.dataset.csSizeOption === this.#selected) ?? options[0];

      options.forEach((option) => {
        const selected = option.dataset.csSizeOption === this.#selected;
        option.classList.toggle('selected', selected);
        option.setAttribute('aria-selected', String(selected));
        option.tabIndex = option === activeOption ? 0 : -1;
      });
    }

    this.#syncNativeProperties();
  }

  #changeUnit(unit) {
    if (unit === this.#unit) return;

    this.#clearMeasurements();

    this.#unit = unit;
    this.#clearValidation();
    this.#syncUnit();
    this.#renderChart();
  }

  #syncUnit() {
    const inch = this.#unit === 'inch';
    this.querySelectorAll('[data-cs-unit-switch]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = !inch;
    });
    this.querySelectorAll('[data-cs-inch-label]').forEach((label) => {
      if (label instanceof HTMLElement) label.style.fontWeight = inch ? '600' : '400';
    });
    this.querySelectorAll('[data-cs-cm-label]').forEach((label) => {
      if (label instanceof HTMLElement) label.style.fontWeight = inch ? '400' : '600';
    });
    this.querySelectorAll('[data-cs-custom-unit]').forEach((label) => {
      label.textContent = inch ? 'IN' : 'CM';
    });
    this.#syncNativeProperties();
  }

  #renderChart() {
    const body = this.querySelector('[data-cs-chart] tbody');
    if (!body) return;

    const keys = this.#unit === 'inch'
      ? ['bustIn', 'waistIn', 'hipsIn', 'hollowIn']
      : ['bustCm', 'waistCm', 'hipsCm', 'hollowCm'];

    body.innerHTML = this.#data
      .map((row) => `<tr><td>${row.us}</td>${keys.map((key) => `<td>${row[key]}</td>`).join('')}</tr>`)
      .join('');
  }

  #toggleDesktop() {
    const list = this.querySelector('[data-cs-dropdown]');
    const trigger = this.querySelector('[data-cs-trigger]');
    if (!list || !trigger) return;

    const open = list.classList.contains('hidden');
    list.classList.toggle('hidden', !open);
    list.setAttribute('aria-hidden', String(!open));
    trigger.setAttribute('aria-expanded', String(open));
    trigger.classList.toggle('cs-size-dress__trigger--open', open);

    this.#outsideController?.abort();
    if (!open) return;

    this.#outsideController = new AbortController();
    document.addEventListener('pointerdown', this.#onOutsidePointerDown, {
      signal: this.#outsideController.signal,
    });
  }

  #closeDesktop(focus = false) {
    const list = this.querySelector('[data-cs-dropdown]');
    const trigger = this.querySelector('[data-cs-trigger]');
    list?.classList.add('hidden');
    list?.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.classList.remove('cs-size-dress__trigger--open');
    this.#outsideController?.abort();
    if (focus && trigger instanceof HTMLElement) trigger.focus();
  }

  #openMobile() {
    const dialog = this.querySelector('[data-cs-dialog]');
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    lockScroll(dialog);
    try {
      dialog.showModal();
      this.querySelector('[data-cs-mobile-trigger]')?.setAttribute('aria-expanded', 'true');

      const option = dialog.querySelector('[data-cs-size-option][tabindex="0"]');
      if (option instanceof HTMLElement) queueMicrotask(() => option.focus());
    } catch (error) {
      unlockScroll(dialog);
      console.error('Unable to open dress size dialog:', error);
    }
  }

  #closeMobile(focus = false) {
    const dialog = this.querySelector('[data-cs-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.open) dialog.close();
    unlockScroll(dialog);

    const trigger = this.querySelector('[data-cs-mobile-trigger]');
    trigger?.setAttribute('aria-expanded', 'false');
    if (focus && trigger instanceof HTMLElement) queueMicrotask(() => trigger.focus());
  }

  #toggleMeasure(button) {
    const box = button.closest('.size-toolbar')?.parentElement?.querySelector('[data-cs-measure-box]');
    if (!box) return;
    const open = box.classList.contains('hidden');
    box.classList.toggle('hidden', !open);
    button.setAttribute('aria-expanded', String(open));
  }

  #clearValidation() {
    this.querySelectorAll('[aria-invalid="true"]').forEach((element) => {
      element.removeAttribute('aria-invalid');
    });
  }

  #clearMeasurements() {
    this.querySelectorAll('[data-cs-measurement]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = '';
    });
  }

  /** @param {FormData} formData */
  #removeCustomProperties(formData) {
    this.querySelectorAll('[data-cs-custom]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const propertyName = input.dataset.csPropertyName || input.name;
      if (propertyName) formData.delete(propertyName);
    });
  }

  /** @returns {Array<[string, string]>} */
  #getPropertyEntries() {
    const control = this.querySelector('[data-cs-size-control]');
    if (!(control instanceof HTMLSelectElement)) return [];

    const size = this.#selected || control.value;
    if (!size) return [];

    /** @type {Array<[string, string]>} */
    const entries = [[control.dataset.csPropertyName || 'properties[Size]', size]];
    if (size !== CUSTOM_SIZE) return entries;

    const unit = this.#unit === 'inch' ? 'in' : 'cm';

    this.querySelectorAll('[data-cs-measurement]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const propertyName = input.dataset.csPropertyName || input.name;
      if (propertyName && input.value !== '') {
        entries.push([propertyName, `${input.value} ${unit}`]);
      }
    });

    return entries;
  }

  #syncNativeProperties() {
    const container = this.#nativePropertiesContainer;
    if (!(container instanceof HTMLElement)) return;

    const fragment = document.createDocumentFragment();
    for (const [name, value] of this.#getPropertyEntries()) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      fragment.appendChild(input);
    }
    container.replaceChildren(fragment);
  }

  /**
   * Writes the current size state into the form submission explicitly. This is
   * also called by product-form-component because quick add renders the size
   * selector outside the form after fetching the product page.
   * @param {FormData} formData
   * @param {HTMLFormElement | null} [form]
   */
  contributeFormData(formData, form = null) {
    if (!(formData instanceof FormData)) return;

    const control = this.querySelector('[data-cs-size-control]');
    if (!(control instanceof HTMLSelectElement)) return;

    const isQuickAdd = Boolean(this.closest('#quick-add-modal-content'));
    if (form && control.form !== form && !isQuickAdd) return;

    formData.delete(control.dataset.csPropertyName || 'properties[Size]');
    this.#removeCustomProperties(formData);
    for (const [name, value] of this.#getPropertyEntries()) formData.set(name, value);
  }

  /**
   * Validates the size selection independently from native form association.
   * Quick add may render these controls outside the product form.
   * @param {HTMLFormElement | null} [form]
   * @returns {boolean}
   */
  validate(form = null) {
    const control = this.querySelector('[data-cs-size-control]');
    if (!(control instanceof HTMLSelectElement)) return true;

    const isQuickAdd = Boolean(this.closest('#quick-add-modal-content'));
    if (form && control.form !== form && !isQuickAdd) return true;

    let valid = control.checkValidity();
    if (this.#selected !== CUSTOM_SIZE) return valid;

    this.querySelectorAll('[data-cs-measurement]').forEach((input) => {
      if (input instanceof HTMLInputElement && !input.checkValidity()) valid = false;
    });

    return valid;
  }

  #focusTrigger() {
    const selector = matchMedia('(max-width: 749px)').matches ? '[data-cs-mobile-trigger]' : '[data-cs-trigger]';
    const trigger = this.querySelector(selector);
    if (trigger instanceof HTMLElement) queueMicrotask(() => trigger.focus());
  }

  #focusFirstInvalidMeasurement() {
    // checkValidity() dispatches an invalid event for every invalid input. Defer
    // focus until that group is complete so only the first field receives focus.
    if (this.#invalidMeasurementFocusScheduled) return;
    this.#invalidMeasurementFocusScheduled = true;

    queueMicrotask(() => {
      this.#invalidMeasurementFocusScheduled = false;
      const input = this.querySelector('[data-cs-measurement]:invalid');
      if (!(input instanceof HTMLInputElement)) return;

      input.focus({ preventScroll: true });
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    const option = target.closest('[data-cs-size-option]');

    if (option instanceof HTMLElement) return this.#select(option.dataset.csSizeOption || '');
    if (target.closest('[data-cs-trigger]')) return this.#toggleDesktop();
    if (target.closest('[data-cs-mobile-trigger]')) return this.#openMobile();
    if (target.closest('[data-cs-dialog-close]')) return this.#closeMobile(true);

    const measure = target.closest('[data-cs-measure-btn]');
    if (measure instanceof HTMLElement) return this.#toggleMeasure(measure);

    if (target.closest('[data-cs-guide-trigger]')) {
      this.querySelector('.cs-size-dress__guide [data-cs-measure-box]')?.classList.add('hidden');
      this.querySelector('.cs-size-dress__guide [data-cs-measure-btn]')?.setAttribute('aria-expanded', 'false');
      return;
    }

    if (target.matches('[data-cs-dialog]')) this.#closeMobile(true);
  };

  #onChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-cs-unit-switch]')) {
      this.#changeUnit(input.checked ? 'cm' : 'inch');
    }
  };

  #onInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-cs-measurement]')) {
      if (event.target.validity.valid) event.target.removeAttribute('aria-invalid');
      this.#syncNativeProperties();
    }
  };

  #onKeyDown = (event) => {
    const trigger = event.target instanceof Element && event.target.closest('[data-cs-trigger]');
    if (trigger && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      const list = this.querySelector('[data-cs-dropdown]');
      if (!list) return;

      event.preventDefault();
      if (list.classList.contains('hidden')) this.#toggleDesktop();
      const items = [...list.querySelectorAll('[data-cs-size-option]')];
      const item = event.key === 'ArrowDown' ? items[0] : items.at(-1);
      if (item instanceof HTMLElement) item.focus();
      return;
    }

    const option = event.target instanceof Element && event.target.closest('[data-cs-size-option]');
    const list = option?.closest('[data-cs-dropdown], [data-cs-dialog-list]');
    if (!(option instanceof HTMLElement) || !list) return;

    const items = [...list.querySelectorAll('[data-cs-size-option]')];
    const index = items.indexOf(option);
    const mobile = list.matches('[data-cs-dialog-list]');

    if (event.key === 'Escape') {
      event.preventDefault();
      if (mobile) this.#closeMobile(true);
      else this.#closeDesktop(true);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      const item = items[nextIndex];
      if (item instanceof HTMLElement) {
        option.tabIndex = -1;
        item.tabIndex = 0;
        item.focus();
      }
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#select(option.dataset.csSizeOption || '');
    }
  };

  #onInvalid = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    const sizeMissing = event.target.matches('[data-cs-size-control]');
    if (sizeMissing) {
      event.preventDefault();
      this.querySelectorAll('[data-cs-trigger], [data-cs-mobile-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-invalid', 'true');
      });
      this.#focusTrigger();
    } else if (event.target.matches('[data-cs-measurement]')) {
      event.target.setAttribute('aria-invalid', 'true');
      this.#focusFirstInvalidMeasurement();
    }
  };

  /** @param {FormDataEvent} event */
  #onFormData = (event) => {
    this.contributeFormData(event.formData, event.currentTarget instanceof HTMLFormElement ? event.currentTarget : null);
  };

  #onPurchaseClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const purchaseControl = event.target.closest(
      'shopify-accelerated-checkout, .shopify-payment-button'
    );
    if (!purchaseControl) return;

    this.#syncNativeProperties();
    const form = this.#getForm();
    if (form && (!form.checkValidity() || !this.validate(form))) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #onFocusOut = (event) => {
    if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#closeDesktop();
  };

  #onOutsidePointerDown = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) this.#closeDesktop();
  };

  #onDialogClose = (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.matches('[data-cs-dialog]')) {
      unlockScroll(event.target);
      this.querySelector('[data-cs-mobile-trigger]')?.setAttribute('aria-expanded', 'false');
    }
  };

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    if (event.detail?.data?.newProduct) queueMicrotask(() => this.#initialize(true));
  };
}

if (!customElements.get('cs-size-dress')) customElements.define('cs-size-dress', CsSizeDress);
